import styled from "styled-components";
import { QUERIES, SPACING } from "../../../../../utils/constants";
import { AmountVariant } from "./types";
import Button from "../../../../../components/Button";

const variantColor = (variant: AmountVariant): string => {
  if (variant === "income") {
    return "var(--color-success)";
  }
  if (variant === "expense") {
    return "var(--color-error)";
  }
  return "var(--color-text-default)";
};

const variantSoftBackground = (variant: AmountVariant): string => {
  if (variant === "income") {
    return "color-mix(in srgb, var(--color-success) 14%, transparent)";
  }
  if (variant === "expense") {
    return "color-mix(in srgb, var(--color-error) 14%, transparent)";
  }
  return "color-mix(in srgb, var(--color-text-secondary) 14%, transparent)";
};

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
`;

export const ManageLink = styled(Button)`
  @media not all and ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const SubtitleLine = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  line-height: 1.4;
`;

export const NetAmount = styled.span<{ $variant: AmountVariant }>`
  font-weight: 700;
  color: ${({ $variant }) => variantColor($variant)};
`;

export const NetUnit = styled.span`
  white-space: nowrap;
`;

export const RecurringGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: 1fr 1fr;
    gap: ${SPACING.md}px;
  }

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

export const RecurringCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.sm + SPACING.xs}px ${SPACING.md}px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-background-0);
  min-width: 0;

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.md}px;
  }
`;

export const Avatar = styled.div<{ $variant: AmountVariant }>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  color: ${({ $variant }) => variantColor($variant)};
  background: ${({ $variant }) => variantSoftBackground($variant)};

  @media ${QUERIES.tabletLargeAndUp} {
    width: 40px;
    height: 40px;
    font-size: var(--text-md);
  }
`;

export const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const CardTitle = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--color-text-default);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs + SPACING.xxs}px;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${QUERIES.tabletAndDown} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0px;
  }
`;

export const FrequencyBadge = styled.span`
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-success);
`;

export const MetaDot = styled.span`
  color: var(--color-text-muted);

  @media ${QUERIES.tabletAndDown} {
    display: none;
  }
`;

export const NextDateText = styled.span`
  color: var(--color-text-secondary);
`;

export const CardAmount = styled.span<{ $variant: AmountVariant }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: ${({ $variant }) => variantColor($variant)};
`;

export const EmptyWrapper = styled.div`
  padding: ${SPACING.md + SPACING.xs}px 0 ${SPACING.xs}px;
  text-align: center;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

export const SkeletonCard = styled.div`
  height: 72px;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    var(--hover-background) 0%,
    var(--color-card-background) 50%,
    var(--hover-background) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
